import React from "react";
import { Forms } from "@/types/form";
import schemas from "@/utils/schemas";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, Resolver, useForm } from "react-hook-form";
import { Params, useNavigate, useParams } from "react-router-dom";
import { Styled } from "./styles";
import { Box, Button, Grid } from "@mui/material";
import constant from "@/utils/constant";
import { useSubmit } from "./hooks";
import { Backdrop } from "@dippi/ui";
import Participants from "./widgets/participants";
import { Campaign } from "@/types/entities";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Base from "./widgets/base";
import Description from "./widgets/description";

export type StepFormValues = Forms["Campaign"] & {
    selectedOrganizationId?: string;
    selectedBrandId?: string;
    selectedProductIds?: string[];
};

const getDefaultValues = (data: Campaign | undefined): Forms["Campaign"] => {
    const values: Forms["Campaign"] = {
        code: data?.code ?? "",
        name: data?.name ?? "",
        description: data?.description ?? "",
        startDate: data?.startDate ? new Date(data.startDate) : new Date(),
        endDate: data?.endDate ? new Date(data.endDate) : new Date(),
        participants: data?.participants ?? {},
    };

    return values;
};

interface Props {
    campaign?: Campaign;
}

const CampaignForm: React.FC<Props> = ({ campaign }) => {
    const navigate = useNavigate();
    const { code } = useParams<Params>();

    const methods = useForm<StepFormValues>({
        resolver: yupResolver(schemas.campaign) as unknown as Resolver<StepFormValues>,
        defaultValues: {
            ...getDefaultValues(campaign),
            selectedOrganizationId: "",
            selectedBrandId: "",
            selectedProductIds: [],
        },
    });

    const { handleSubmit, formState } = methods;
    const { isDirty, isSubmitting } = formState;

    const { mutateAsync: onSubmit } = useSubmit(campaign?._id as string);
    const SPACING = constant.GRID_SPACING;
    const submitBtnText = code ? "Update" : "Create";

    const onCancel = () => {
        navigate(`/campaigns`);
    };

    return (
        <FormProvider {...methods}>
            <Styled.Form
                id="form-campaign"
                onSubmit={handleSubmit(async data => await onSubmit(data))}
            >
                <Grid container item xs={12} spacing={SPACING} rowGap={SPACING}>
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Box>
                            <Button
                                variant="contained"
                                onClick={() => navigate(-1)}
                                startIcon={<ArrowBackIcon />}
                            >
                                Campaigns
                            </Button>
                        </Box>
                        <Box display="flex" gap={SPACING}>
                            <Button variant="outlined" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={!isDirty}>
                                {submitBtnText}
                            </Button>
                        </Box>
                    </Box>
                    <Base />
                    <Description />
                    {code && <Participants />}
                </Grid>
                <Backdrop loading={isSubmitting} />
            </Styled.Form>
        </FormProvider>
    );
};

export default CampaignForm;
